import { reactive, toRefs } from 'vue'
import {
  Threads,
  Thread,
  Question,
  Message,
  InProgressIds,
  CurrentThread,
  Answer,
  Notification,
} from '@/components/chat/types.ts'
import { defineStore } from 'pinia'
import { instance, getApiDomain } from '@cognitivecredit/ui-utils'

const defaultInProgressIds: InProgressIds = {
  questionId: undefined,
  messageId: undefined,
  streamingId: undefined,
}

const getNewMessage = (question: Question, messageId: string): Message => {
  return {
    id: messageId,
    question,
    isCollapsed: false,
    isStreaming: true,
    answer: undefined,
    sources: [],
  }
}

export const useChatAppStore = defineStore('ChatApp', () => {
  const state = reactive({
    threads: [] as Threads,
    currentThread: undefined as CurrentThread | undefined,
    queuedQuestions: [] as string[],
    allCollapsed: false,
    inProgressIds: defaultInProgressIds,
    sessionId: undefined as string | undefined,
    fetchThreadsError: undefined as string | undefined,
    notification: undefined as Notification | undefined,
    showDiscard: undefined as { show: boolean; redirectTo: any } | undefined,
    reader: undefined as any,
  })

  const fetchThreadsHistory = async () => {
    if (state.fetchThreadsError) {
      state.fetchThreadsError = undefined
    }

    try {
      const req = await instance({ apiDomain: getApiDomain('chat-ai') })
      const res = await req.get('/ai/threads')
      state.threads = res.data
      return res.data
    } catch (error) {
      state.fetchThreadsError = 'Error loading chat history. Please try again later.'
      console.error('Error fetching chat history threads:', error)
    }
  }

  const addThreadToHistory = (thread: Thread) => {
    const indexOfFirstUnpinned = state.threads.findIndex((t) => !t.pin)
    if (indexOfFirstUnpinned === -1) {
      state.threads.unshift(thread)
    } else {
      state.threads.splice(indexOfFirstUnpinned, 0, thread)
    }
  }

  const updateThreadsHistory = (id: string, value: any, key?: string) => {
    const index = state.threads.findIndex((thread) => thread.id === id)
    if (index !== -1) {
      if (key) {
        state.threads[index][key] = value
      } else {
        state.threads[index] = value
      }
    }
  }

  const setInProgressIds = (ids?: InProgressIds) => {
    if (!ids) {
      state.inProgressIds = { ...defaultInProgressIds }
    } else {
      state.inProgressIds = { ...state.inProgressIds, ...ids }
    }
  }

  const setCurrentThread = (thread?: CurrentThread) => {
    setInProgressIds()
    state.currentThread = thread
  }

  const queueQuestion = (question: string) => {
    state.queuedQuestions.push(question)
  }

  const unqueueQuestion = () => {
    state.queuedQuestions.shift()
  }

  const createCurrentThread = (id: string, title: string, question: Question, createdAt: string) => {
    const messageId = state.inProgressIds.messageId as string
    const newMessage = getNewMessage(question, messageId)
    setInProgressIds({ questionId: question.id, messageId, streamingId: messageId })
    const newThread: CurrentThread = {
      id,
      title,
      pin: false,
      deleted: false,
      createdAt: createdAt,
      messages: [newMessage],
    }
    state.currentThread = newThread
    // Add the new thread to the sidebar by updating threads history
    addThreadToHistory(newThread)
  }

  const addMessageToCurrentThread = (question: Question) => {
    if (!state.currentThread) {
      return
    }
    const messageId = state.inProgressIds.messageId as string
    const msg = getNewMessage(question, messageId)
    setInProgressIds({ questionId: question.id, messageId, streamingId: messageId })
    // Find an existing message marked as initial and replace it
    const messageIndex = state.currentThread?.messages.findIndex((m) => m.isInitial)
    state.currentThread.messages[messageIndex] = msg
    return msg
  }

  const updateMessage = (
    thread: CurrentThread,
    messageId: string,
    update: { key: string; value: any; nestedKey?: string },
  ) => {
    const i = thread.messages.findIndex((m) => m.id === messageId)
    if (i === -1) {
      return
    }

    const msg = thread.messages[i]

    const updated: Message = { ...msg }

    if (update.nestedKey) {
      updated[update.key] = {
        ...msg[update.key],
        [update.nestedKey]: update.value,
      }
    } else {
      updated[update.key] = update.value
    }

    thread.messages[i] = updated
  }

  const updateCurrentThreadMessageAnswer = (answer: Answer) => {
    if (!state.currentThread || !state.inProgressIds.messageId) {
      return
    }

    updateMessage(state.currentThread, state.inProgressIds.messageId, {
      key: 'answer',
      value: answer,
    })
  }

  const syncCollapseAllState = () => {
    const allMessagesCollapsed = state.currentThread?.messages.every((m) => m.isCollapsed)
    const someMessagesExpanded = state.currentThread?.messages.some((m) => !m.isCollapsed)
    if (allMessagesCollapsed) {
      state.allCollapsed = true
    } else if (someMessagesExpanded) {
      state.allCollapsed = false
    }
  }

  const updateCurrentThreadMessages = (messageId: string, key: string, value: any, nestedKey?: string) => {
    if (!state.currentThread) {
      return
    }
    updateMessage(state.currentThread, messageId, { key, value, nestedKey })

    if (key === 'isCollapsed') {
      syncCollapseAllState()
    }
  }

  const setCollapseAll = () => {
    state.allCollapsed = !state.allCollapsed
    state.currentThread?.messages.forEach((m) => {
      m.isCollapsed = state.allCollapsed
    })
  }

  const resetCurrentThreadState = async () => {
    state.currentThread = undefined
    state.allCollapsed = false
    state.inProgressIds = defaultInProgressIds
    state.notification = undefined
    await cancelReader()
  }

  const cancelReader = async () => {
    if (state.reader) {
      await state.reader.cancel()
      state.reader = undefined
    }
  }

  const setReader = (reader: any) => {
    state.reader = reader
  }

  const setNotification = (notification) => {
    state.notification = notification
  }

  const setShowDiscard = (discard) => {
    state.showDiscard = discard
  }

  const setSessionId = (sessionId: string) => {
    state.sessionId = sessionId
  }

  return {
    ...toRefs(state),
    fetchThreadsHistory,
    addThreadToHistory,
    updateThreadsHistory,
    setCurrentThread,
    createCurrentThread,
    addMessageToCurrentThread,
    updateCurrentThreadMessageAnswer,
    updateCurrentThreadMessages,
    setCollapseAll,
    setInProgressIds,
    queueQuestion,
    unqueueQuestion,
    resetCurrentThreadState,
    setNotification,
    setShowDiscard,
    setSessionId,
    setReader,
    cancelReader,
  }
})
